// StudyCompanion.js
// AI-powered Study Companion for YouTube videos
// Shows synchronized AI insights and allows Q&A while watching

import { BookOpen, Clock, Lightbulb, Loader, MessageCircle, Send, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import YouTube from 'react-youtube';

const StudyCompanion = ({ videoInfo, onClose, darkMode, makeOpenAIRequest }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [insights, setInsights] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoadingInsight, setIsLoadingInsight] = useState(false);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [autoInsights, setAutoInsights] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const playerRef = useRef(null);
  const chatEndRef = useRef(null);

  // Initialize insights for different timestamps
  useEffect(() => {
    generateInitialInsights();
  }, [videoInfo]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const generateInitialInsights = async () => {
    setIsLoadingInsight(true);
    try {
      const messages = [
        {
          role: 'system',
          content: `You are an educational AI assistant analyzing a YouTube video. Generate 5-7 key insights that would appear at different timestamps throughout the video. Each insight should highlight important concepts, formulas, or definitions.

Return JSON array:
[
  {
    "timestamp": 0,
    "title": "Introduction",
    "content": "Brief insight about what's being introduced",
    "type": "concept" // or "formula", "definition", "tip"
  }
]`
        },
        {
          role: 'user',
          content: `Generate educational insights for this video:

Title: ${videoInfo.title}
Description: ${videoInfo.description}
Tags: ${videoInfo.tags?.join(', ')}

Create insights that appear at key moments (start, 25%, 50%, 75%, end) with important concepts, formulas, or definitions.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 1500);
      const generatedInsights = JSON.parse(response);
      setInsights(generatedInsights);
    } catch (error) {
      console.error('Error generating insights:', error);
      // Provide default insights
      setInsights([
        {
          timestamp: 0,
          title: 'Getting Started',
          content: 'This video covers important educational content. Pay attention to key concepts and take notes!',
          type: 'tip'
        }
      ]);
    }
    setIsLoadingInsight(false);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoadingMessage) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: currentTime
    };

    setMessages([...messages, userMessage]);
    setInputMessage('');
    setIsLoadingMessage(true);

    try {
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const aiMessages = [
        {
          role: 'system',
          content: `You are an educational AI assistant helping a student understand a YouTube video. The student is currently at ${Math.floor(currentTime / 60)}:${String(Math.floor(currentTime % 60)).padStart(2, '0')} in the video.

Video Information:
Title: ${videoInfo.title}
Description: ${videoInfo.description}

Provide clear, concise explanations. If asked about formulas or concepts, explain them step-by-step. Be encouraging and helpful.`
        },
        ...conversationHistory,
        {
          role: 'user',
          content: inputMessage
        }
      ];

      const response = await makeOpenAIRequest(aiMessages, 800);

      const aiMessage = {
        role: 'assistant',
        content: response,
        timestamp: currentTime
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: currentTime
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsLoadingMessage(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getCurrentInsight = () => {
    if (insights.length === 0) return null;

    // Find the most recent insight based on current time
    const relevantInsights = insights.filter(i => i.timestamp <= currentTime);
    if (relevantInsights.length === 0) return insights[0];

    return relevantInsights[relevantInsights.length - 1];
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'formula': return '📐';
      case 'definition': return '📖';
      case 'concept': return '💡';
      case 'tip': return '✨';
      default: return '📌';
    }
  };

  const currentInsight = getCurrentInsight();

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`w-full max-w-7xl h-[90vh] rounded-3xl shadow-2xl overflow-hidden ${
        darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-purple-500" />
            <div>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Study Companion
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {videoInfo.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="flex h-[calc(100%-80px)]">
          {/* Left Column - Video Player */}
          <div className={`flex-1 p-6 border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {/* YouTube Embed */}
            <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-black relative">
              <YouTube
                videoId={videoInfo.id}
                opts={{
                  width: '100%',
                  height: '100%',
                  playerVars: {
                    autoplay: 0,
                    modestbranding: 1,
                    rel: 0
                  }
                }}
                onReady={(event) => {
                  playerRef.current = event.target;
                  setVideoError(false);
                }}
                onError={(event) => {
                  setVideoError(true);
                }}
                onStateChange={(event) => {
                  // Track video time
                  if (event.data === 1) { // Playing
                    const interval = setInterval(() => {
                      if (playerRef.current) {
                        playerRef.current.getCurrentTime().then(time => {
                          setCurrentTime(time);
                        });
                      }
                    }, 1000);
                    return () => clearInterval(interval);
                  }
                }}
                className="w-full h-full"
              />

              {/* Error notification for blocked content */}
              {videoError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900/95 p-6">
                  <div className="text-center max-w-md">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <span className="text-3xl">⚠️</span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      Video Player Blocked
                    </h3>
                    <p className="text-gray-300 mb-4">
                      It looks like a browser extension (such as an ad blocker or content blocker) is preventing the YouTube video from loading.
                    </p>
                    <div className={`p-4 rounded-lg text-left mb-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-700'}`}>
                      <p className="text-sm text-gray-300 mb-2 font-semibold">To fix this:</p>
                      <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
                        <li>Disable your ad blocker or content blocker for this site</li>
                        <li>Check if YouTube is blocked in your browser extensions</li>
                        <li>Try opening the video directly on YouTube using the link below</li>
                      </ul>
                    </div>
                    <a
                      href={`https://www.youtube.com/watch?v=${videoInfo.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      <span>Open on YouTube</span>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                      </svg>
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Current AI Insight Card */}
            {currentInsight && autoInsights && (
              <div className={`p-4 rounded-xl border-2 ${
                darkMode
                  ? 'bg-purple-900/20 border-purple-500/50'
                  : 'bg-purple-50 border-purple-200'
              }`}>
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{getInsightIcon(currentInsight.type)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-purple-500" />
                      <span className={`text-sm font-medium ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                        {formatTime(currentInsight.timestamp)}
                      </span>
                    </div>
                    <h4 className={`font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {currentInsight.title}
                    </h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {currentInsight.content}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* All Insights Timeline */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className={`font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <BookOpen className="w-4 h-4" />
                  Key Insights Timeline
                </h3>
                <button
                  onClick={() => setAutoInsights(!autoInsights)}
                  className={`text-xs px-3 py-1 rounded-full ${
                    autoInsights
                      ? 'bg-purple-500 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {autoInsights ? 'Auto On' : 'Auto Off'}
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {insights.map((insight, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border text-sm ${
                      darkMode
                        ? 'bg-gray-800 border-gray-700 hover:bg-gray-750'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    } cursor-pointer transition-colors`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span>{getInsightIcon(insight.type)}</span>
                      <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {insight.title}
                      </span>
                      <span className={`ml-auto text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formatTime(insight.timestamp)}
                      </span>
                    </div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {insight.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Chat Interface */}
          <div className="w-96 flex flex-col">
            {/* Chat Header */}
            <div className={`px-4 py-3 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <MessageCircle className="w-5 h-5 text-blue-500" />
                Ask Questions
              </h3>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Ask me anything about the video content
              </p>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Lightbulb className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Ask me to explain concepts, formulas, or definitions from the video!
                  </p>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      message.role === 'user'
                        ? 'bg-blue-500 text-white'
                        : darkMode
                        ? 'bg-gray-800 text-gray-100'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <span className={`text-xs mt-1 block ${
                      message.role === 'user' ? 'text-blue-100' : darkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                </div>
              ))}

              {isLoadingMessage && (
                <div className="flex justify-start">
                  <div className={`rounded-2xl px-4 py-3 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <Loader className="w-5 h-5 animate-spin text-blue-500" />
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div className={`p-4 border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Ask about the video..."
                  disabled={isLoadingMessage}
                  className={`flex-1 px-4 py-2 rounded-xl border ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoadingMessage}
                  className="px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyCompanion;
